import PropTypes from 'prop-types';
import { useTranslation } from 'react-i18next';
import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { CheckIcon, Loader2Icon, XIcon } from 'lucide-react';

import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import {
  Popover,
  PopoverAnchor,
  PopoverContent,
} from '@/components/ui/popover';
import { cn } from '@/lib/utils';
import { useAutocompleteQuery } from '../../services/api';

AutocompleteInput.propTypes = {
  onChange: PropTypes.func,
  label: PropTypes.string,
  resource: PropTypes.string,
  value: PropTypes.any,
  defaultValue: PropTypes.any,
  required: PropTypes.bool,
  error: PropTypes.any,
  query: PropTypes.string,
  refreshOnInputChange: PropTypes.bool,
  multiple: PropTypes.bool,
  getOptionLabel: PropTypes.any,
  disabled: PropTypes.bool,
  freeSolo: PropTypes.any,
  onInputChange: PropTypes.func,
  canDelete: PropTypes.bool,
  onKeyDown: PropTypes.func,
  clearOnBlur: PropTypes.bool,
};

const DEBOUNCE_MS = 250;

function defaultGetOptionLabel(option) {
  if (option == null) return '';
  if (typeof option === 'string') return option;
  return option.title ?? option.name ?? '';
}

/*
 * shadcn-based AutocompleteInput. Drop-in replacement for the legacy
 * MUI `<Autocomplete>` wrapper — same props, same call semantics from the
 * consumer's perspective:
 *
 *   - `resource`: backend autocomplete endpoint, e.g. "resource/name-autocomplete".
 *   - `value` / `defaultValue`: option object `{ id, title }` (or a string in
 *     freeSolo mode). The input snaps to its label on selection.
 *   - `onChange(option)`: fired when the user picks an option from the
 *     dropdown OR — in freeSolo mode — types a value (also called with the
 *     typed string).
 *   - `onInputChange(text)`: fired on every keystroke so freeSolo consumers
 *     can read the current draft text.
 *   - `freeSolo`: allows the input text to act as the value when no option
 *     is selected.
 *   - `clearOnBlur`: when the popover closes without a selection AND the
 *     consumer is NOT freeSolo, the input snaps back to the selected value's
 *     label (or empty).
 *   - `error`: truthy applies a destructive ring on the input.
 *
 * Internals are shadcn Input + Popover; same z-index tier as the other
 * shadcn popovers/dialogs so dropdowns paint above EntitySheets without any
 * per-call-site adjustments.
 */
export default function AutocompleteInput({
  onChange,
  label,
  resource,
  value,
  defaultValue,
  required,
  error,
  query,
  refreshOnInputChange = false,
  multiple = false,
  getOptionLabel,
  disabled,
  freeSolo = false,
  onInputChange,
  canDelete = true,
  onKeyDown,
  clearOnBlur = true,
}) {
  const { t } = useTranslation();

  // Legacy positional shims — kept so call sites compile, but unused.
  void refreshOnInputChange;
  void multiple;
  void query;

  const getLabel = useMemo(
    () => (typeof getOptionLabel === 'function' ? getOptionLabel : defaultGetOptionLabel),
    [getOptionLabel],
  );

  const initialValue = value ?? defaultValue ?? null;

  // Track the parent's selected value (controlled-ish) plus the draft text
  // so the user can type freely without round-tripping every keystroke
  // through the parent.
  const [selected, setSelected] = useState(initialValue);
  const [inputText, setInputText] = useState(getLabel(initialValue));
  const [open, setOpen] = useState(false);
  const [debouncedSearch, setDebouncedSearch] = useState('');
  const inputRef = useRef(null);

  // Sync from external `value` changes (parent reset / Cancel / form clear).
  useEffect(() => {
    setSelected(value ?? null);
    setInputText(getLabel(value ?? null));
  }, [value, getLabel]);

  // Debounce — refetch DEBOUNCE_MS after the user stops typing.
  useEffect(() => {
    if (!open) return undefined;
    const id = window.setTimeout(() => setDebouncedSearch(inputText), DEBOUNCE_MS);
    return () => window.clearTimeout(id);
  }, [open, inputText]);

  const { data: results, isFetching } = useAutocompleteQuery(
    { resource, q: debouncedSearch },
    { skip: !open || !resource },
  );

  const options = Array.isArray(results) ? results : [];

  const handleSelect = useCallback(
    (option) => {
      setSelected(option);
      setInputText(getLabel(option));
      setOpen(false);
      if (onChange) onChange(option);
    },
    [getLabel, onChange],
  );

  const handleClear = useCallback(() => {
    setSelected(null);
    setInputText('');
    if (onChange) onChange(null);
    if (onInputChange) onInputChange('');
  }, [onChange, onInputChange]);

  const handleInputChange = useCallback(
    (e) => {
      const next = e.target.value;
      setInputText(next);
      if (!open) setOpen(true);
      if (onInputChange) onInputChange(next);
      // freeSolo: the typed text IS the value — surface it via onChange so
      // the consumer's setState fires.
      if (freeSolo && onChange) onChange(next);
    },
    [freeSolo, onChange, onInputChange, open],
  );

  const handleOpenChange = useCallback(
    (next) => {
      setOpen(next);
      if (!next) {
        if (!freeSolo && clearOnBlur) {
          setInputText(getLabel(selected));
        }
      }
    },
    [freeSolo, clearOnBlur, getLabel, selected],
  );

  const handleInteractOutside = useCallback((e) => {
    const target = e.target;
    if (target instanceof Node && inputRef.current?.contains(target)) {
      e.preventDefault();
    }
  }, []);

  const showSpinner = isFetching && options.length === 0;
  const showClear =
    canDelete && (selected || (freeSolo && inputText.length > 0)) && !disabled;
  const hasError = Boolean(error);

  return (
    <div className="space-y-1">
      {label ? (
        <Label
          className={cn(
            'text-sm font-medium',
            required && "after:ml-0.5 after:text-destructive after:content-['*']",
          )}
        >
          {label}
        </Label>
      ) : null}

      <Popover open={open} onOpenChange={handleOpenChange}>
        <PopoverAnchor asChild>
          <div className="relative">
            <Input
              ref={inputRef}
              value={inputText}
              onChange={handleInputChange}
              onFocus={() => !disabled && setOpen(true)}
              onClick={() => !disabled && setOpen(true)}
              onKeyDown={onKeyDown}
              disabled={disabled}
              autoComplete="off"
              spellCheck={false}
              aria-invalid={hasError}
              className={cn(
                showClear && 'pr-8',
                hasError && 'border-destructive ring-destructive/20',
              )}
            />
            {showClear ? (
              <button
                type="button"
                onClick={handleClear}
                aria-label={t('common.clear', 'Clear')}
                className="absolute top-1/2 right-2 -translate-y-1/2 rounded p-0.5 text-muted-foreground transition-colors hover:text-foreground"
              >
                <XIcon className="size-3.5" />
              </button>
            ) : null}
          </div>
        </PopoverAnchor>
        <PopoverContent
          className="w-[var(--radix-popover-trigger-width)] p-1"
          align="start"
          onOpenAutoFocus={(e) => e.preventDefault()}
          onInteractOutside={handleInteractOutside}
        >
          {showSpinner ? (
            <div className="flex items-center justify-center gap-2 py-6 text-sm text-muted-foreground">
              <Loader2Icon className="size-4 animate-spin" />
              {t('common.loading')}
            </div>
          ) : options.length === 0 ? (
            <div className="py-6 text-center text-sm text-muted-foreground">
              {t('common.no-options')}
            </div>
          ) : (
            <ul role="listbox" className="max-h-64 overflow-auto">
              {options.map((option, index) => {
                const optLabel = getLabel(option);
                const isSelected =
                  selected && selected.id != null && option.id === selected.id;
                return (
                  <li
                    key={option.id ?? `${optLabel}-${index}`}
                    role="option"
                    aria-selected={Boolean(isSelected)}
                    onMouseDown={(e) => {
                      e.preventDefault();
                      handleSelect(option);
                    }}
                    className={cn(
                      'flex cursor-pointer items-center rounded px-2 py-1.5 text-sm hover:bg-accent',
                      isSelected && 'bg-accent/60',
                    )}
                  >
                    <CheckIcon
                      className={cn(
                        'mr-2 size-4',
                        isSelected ? 'opacity-100' : 'opacity-0',
                      )}
                    />
                    {optLabel}
                  </li>
                );
              })}
            </ul>
          )}
        </PopoverContent>
      </Popover>
    </div>
  );
}
